import { createContext, useContext, useState, useEffect } from 'react';
import { defaultCharacter } from './characterDefaults.js';
import {
  makeFieldUpdater,
  applyCurrentHealth,
  applyTotalHealth,
  applyTraitValue,
} from './characterUpdaters.js';
import { useCharacterPersistence } from './useCharacterPersistence.js';

const CharacterInfo = createContext(null);

export const CharacterProvider = ({ children, index }) => {
  const [character, setCharacter] = useState(defaultCharacter);
  const { loaded, loadCharacter, saveCharacter } = useCharacterPersistence(
    index,
    character,
    setCharacter
  );

  const updateName = makeFieldUpdater(setCharacter, 'name');
  const updateAge = makeFieldUpdater(setCharacter, 'age');
  const updateGender = makeFieldUpdater(setCharacter, 'gender');
  const updateHeightM = makeFieldUpdater(setCharacter, 'heightM');
  const updateHeightFt = makeFieldUpdater(setCharacter, 'heightFt');
  const updateHeightIn = makeFieldUpdater(setCharacter, 'heightIn');
  const updateWeightKg = makeFieldUpdater(setCharacter, 'weightKg');
  const updateWeightLbs = makeFieldUpdater(setCharacter, 'weightLbs');
  const updateSpecies = makeFieldUpdater(setCharacter, 'species');
  const updateIsImperial = makeFieldUpdater(setCharacter, 'isImperial');
  const updateTotalResilience = makeFieldUpdater(
    setCharacter,
    'totalResilience'
  );
  const updateAvailablePoints = makeFieldUpdater(
    setCharacter,
    'availablePoints'
  );
  const updateUsedPoints = makeFieldUpdater(setCharacter, 'usedPoints');
  const updateUnsavedChanges = makeFieldUpdater(setCharacter, 'unsavedChanges');

  const updateCurrentResilience = newCurrentResilience => {
    setCharacter(prev => ({
      ...prev,
      currentResilience: newCurrentResilience,
    }));
    character.currentResilience = newCurrentResilience;
  };

  const updateCurrentHealth = newCurrentHealth => {
    setCharacter(prev => applyCurrentHealth(prev, newCurrentHealth));
    character.currentHealth = newCurrentHealth;
  };

  const updateTotalHealth = newTotalHealth => {
    setCharacter(prev => applyTotalHealth(prev, newTotalHealth));
  };

  const updateLuckPoints = newLuckPoints => {
    setCharacter(prev => ({ ...prev, luckPoints: newLuckPoints }));
    character.luckPoints = newLuckPoints;
  };

  const updateTraitValue = (name, value) => {
    setCharacter(prev => applyTraitValue(prev, name, value));
  };

  const addToUsedTraits = name => {
    setCharacter(prev => ({ ...prev, usedTraits: [...prev.usedTraits, name] }));
    character.usedTraits.push(name);
  };

  const clearUsedTraits = () => {
    setCharacter(prev => ({ ...prev, usedTraits: [] }));
    character.usedTraits = [];
  };

  const updateSelectedHealTrait = newSelectedHealTrait => {
    setCharacter(prev => ({
      ...prev,
      selectedHealTrait: newSelectedHealTrait,
    }));
    character.selectedHealTrait = newSelectedHealTrait;
    saveCharacter();
  };

  const updateSelectedRepairTrait = newSelectedRepairTrait => {
    setCharacter(prev => ({
      ...prev,
      selectedRepairTrait: newSelectedRepairTrait,
    }));
    character.selectedRepairTrait = newSelectedRepairTrait;
    saveCharacter();
  };

  const addGearToStorage = gear => {
    setCharacter(prev => ({
      ...prev,
      storage: [...prev.storage, gear],
    }));
    character.storage = [...character.storage, gear];
    saveCharacter();
  };

  const removeGearFromStorage = gearId => {
    setCharacter(prev => ({
      ...prev,
      storage: prev.storage.filter(g => g.id !== gearId),
    }));
    character.storage = character.storage.filter(g => g.id !== gearId);
    saveCharacter();
  };

  const updateGearUses = (gearId, remainingUses) => {
    setCharacter(prev => ({
      ...prev,
      storage: prev.storage.map(g =>
        g.id === gearId ? { ...g, remainingUses } : g
      ),
    }));
    character.storage = character.storage.map(g =>
      g.id === gearId ? { ...g, remainingUses } : g
    );
    saveCharacter();
  };

  const updatePrimaryWeaponId = weaponId => {
    setCharacter(prev => ({ ...prev, primaryWeaponId: weaponId }));
    character.primaryWeaponId = weaponId;
  };
  const updateSecondaryWeaponId = weaponId => {
    setCharacter(prev => ({ ...prev, secondaryWeaponId: weaponId }));
    character.secondaryWeaponId = weaponId;
  };
  const updateEquipmentId = equipId => {
    setCharacter(prev => ({ ...prev, equipmentId: equipId }));
    character.equipmentId = equipId;
  };

  const updateWeaponBonus = (weaponId, bonus) => {
    setCharacter(prev => {
      const updatedStorage = prev.storage.map(gear => {
        if (gear.id === weaponId) {
          return { ...gear, bonus };
        }
        return gear;
      });
      return { ...prev, storage: updatedStorage };
    });
    saveCharacter();
  };

  useEffect(() => {
    loadCharacter();
  }, [index, loadCharacter]);

  return (
    <CharacterInfo.Provider
      value={{
        character,
        updateName,
        updateAge,
        updateGender,
        updateHeightM,
        updateHeightFt,
        updateHeightIn,
        updateWeightKg,
        updateWeightLbs,
        updateSpecies,
        updateIsImperial,
        updateCurrentResilience,
        updateTotalResilience,
        updateCurrentHealth,
        updateTotalHealth,
        updateAvailablePoints,
        updateUsedPoints,
        updateLuckPoints,
        updateTraitValue,
        updateUnsavedChanges,
        saveCharacter,

        updateSelectedHealTrait,
        updateSelectedRepairTrait,
        addToUsedTraits,
        clearUsedTraits,

        addGearToStorage,
        removeGearFromStorage,
        updatePrimaryWeaponId,
        updateSecondaryWeaponId,
        updateEquipmentId,
        updateWeaponBonus,
        updateGearUses,

        loaded,
      }}
    >
      {children}
    </CharacterInfo.Provider>
  );
};

export const useCharacter = () => useContext(CharacterInfo);
